/** Labelled slider + numeric input row used across adjustment
 *  panels. Designed for parameters that have a clear numeric range
 *  and a precise value the user might want to type. The slider is
 *  the primary input; the number is for fine-tuning and feedback. */
export interface RangeRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  /** Number of decimal places to render in the numeric input. Use
   *  0 for integer params (Black point, channel mix), 2 for floats
   *  (Gamma). Default 0. */
  decimals?: number;
  onChange: (v: number) => void;
}

export function RangeRow({ label, min, max, step, value, decimals = 0, onChange }: RangeRowProps) {
  const display = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs w-16 text-muted-foreground">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={display}
        onChange={(e) => {
          const n = Number(e.target.value);
          // Clamp typed values to the slider range. Without this,
          // typing e.g. `9999` into a -100..+100 field passes
          // through and breaks the downstream math (e.g. Levels
          // gamma collapses, B/C contrast factor blows up).
          if (Number.isFinite(n)) onChange(n < min ? min : n > max ? max : n);
        }}
        className="w-14 text-xs text-right tabular-nums bg-muted rounded px-1 py-0.5 border border-border"
      />
    </div>
  );
}
